import React from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Tags,
  Boxes,
  Receipt,
  Truck,
  Mail,
  Film,
  Percent,
  LogOut,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/categories", icon: Tags, label: "Categories" },
  { to: "/admin/inventory", icon: Boxes, label: "Inventory" },
  { to: "/admin/pricing", icon: Percent, label: "Shipping & Discounts" },
  { to: "/admin/orders", icon: Receipt, label: "Orders" },
  { to: "/admin/delivery", icon: Truck, label: "Delivery Status" },
  { to: "/admin/contacts", icon: Mail, label: "Contact Forms" },
  { to: "/admin/multimedia", icon: Film, label: "Multimedia" },
];

export default function AdminLayout() {
  const { session, logout } = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#F4F1EA]">
      <aside
        data-testid="admin-sidebar"
        className="w-64 bg-[#281a12] text-[#FDFBF7] flex flex-col"
      >
        <Link
          to="/admin"
          data-testid="admin-sidebar-logo"
          className="px-6 py-6 border-b border-[#FDFBF7]/10 flex items-center gap-3"
        >
          <span className="w-9 h-9 border border-[#946f36] text-[#946f36] font-serif-display flex items-center justify-center">
            A
          </span>
          <div className="leading-tight">
            <div className="font-serif-display text-lg">Ace Asia</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#946f36]">
              Admin Console
            </div>
          </div>
        </Link>

        <nav className="flex-1 py-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  isActive
                    ? "bg-[#946f36]/15 text-[#FDFBF7] border-l-2 border-[#946f36]"
                    : "text-[#D1C3B4] hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/5 border-l-2 border-transparent"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-6 py-5 border-t border-[#FDFBF7]/10 text-xs">
          <div className="text-[#D1C3B4] mb-1">Signed in as</div>
          <div className="text-[#FDFBF7] truncate" data-testid="admin-current-user">
            {session?.email}
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="mt-4 inline-flex items-center gap-2 text-[#946f36] hover:text-[#FDFBF7]"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-[#FDFBF7] border-b border-[#6e5022]/15 px-8 py-4 flex items-center justify-between">
          <div className="text-sm text-[#4A3728]">
            <span className="overline text-[#946f36] mr-3">Admin</span>
            <span>Manage catalogue, orders, and storytelling.</span>
          </div>
          <Link
            to="/"
            data-testid="admin-view-site-link"
            className="text-xs uppercase tracking-[0.18em] text-[#6e5022] hover:text-[#281a12]"
          >
            View Site →
          </Link>
        </header>
        <main className="flex-1 px-8 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
